import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";

import { cn } from "@/lib/utils";

const ProviderAbove = React.createContext(false);

function TooltipProvider(props: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <ProviderAbove.Provider value={true}>
      <TooltipPrimitive.Provider {...props} />
    </ProviderAbove.Provider>
  );
}

/**
 * Inside the app layout the sidebar's provider sets one delay and lets neighbouring tooltips
 * skip it, so a tooltip uses that one; on a page outside the layout (public, sign-in) or in a
 * test it brings its own.
 */
function Tooltip(props: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  const root = <TooltipPrimitive.Root {...props} />;
  return React.useContext(ProviderAbove) ? root : <TooltipPrimitive.Provider>{root}</TooltipPrimitive.Provider>;
}

const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, collisionPadding = 8, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      collisionPadding={collisionPadding}
      className={cn(
        // Plain labels are inverted (spec). Rich previews mark their body
        // data-tooltip-card and get the popover pair, so status colours stay legible.
        "has-[[data-tooltip-card]]:border has-[[data-tooltip-card]]:border-border has-[[data-tooltip-card]]:bg-popover has-[[data-tooltip-card]]:text-popover-foreground",
        "z-floating overflow-hidden rounded-[4px] bg-foreground text-background px-2 py-1 text-[13px] shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-tooltip-content-transform-origin]",
        className
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = "TooltipContent";

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
